import * as XLSX from "xlsx";
import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-hot-toast";
import {
  FiUsers,
  FiPlus,
  FiChevronLeft,
  FiChevronRight,
  FiRefreshCw,
  FiX,
  FiEdit,
  FiDownload,
  FiLock,
} from "react-icons/fi";
import {
  fetchULBUsers,
  createULBUser,
  updateULBUser,
  resetULBUserPassword,
} from "../../redux/actions/user-management/ulbUserActions";
import {
  fetchDistricts,
  fetchMunicipalities,
  clearMunicipalities,
} from "../../redux/actions/internal/internalActions";

const ULBUsers = () => {
  const dispatch = useDispatch();
  const { loading, users, pagination } = useSelector(
    (state) =>
      state.ulbUser || {
        loading: false,
        users: [],
        pagination: { page: 1, limit: 10, totalRecords: 0, totalPages: 0 },
      },
  );

  const { districts, municipalities } = useSelector(
    (state) => state.internal || { districts: [], municipalities: [] },
  );

  const [currentPage, setCurrentPage] = useState(1);
  const [filters, setFilters] = useState({
    district_id: "",
    municipality_id: "",
  });
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [phoneError, setPhoneError] = useState("");
  const [editPhoneError, setEditPhoneError] = useState("");
  const [limit, setLimit] = useState(10);
  const limitOptions = [
    10, 20, 50, 100, 200, 500, 1000, 1500, 2000, 3000, 4000,
  ];

  const [formData, setFormData] = useState({
    full_name: "",
    designation: "",
    username: "",
    email: "",
    phone: "",
    district_id: "",
    municipality_id: "",
  });

  const [passwordData, setPasswordData] = useState({
    newPassword: "",
    confirmPassword: "",
  });
  const validatePhone = (value) => {
    return value.replace(/\D/g, "").slice(0, 10);
  };
  const isValidPhone = (phone) => {
    return phone.length === 10;
  };
  useEffect(() => {
    dispatch(fetchULBUsers(currentPage, limit, filters));
    dispatch(fetchDistricts());
  }, [
    dispatch,
    currentPage,
    limit,
    filters.district_id,
    filters.municipality_id,
  ]);

  useEffect(() => {
    if (filters.district_id) {
      dispatch(fetchMunicipalities(filters.district_id));
    } else {
      dispatch(clearMunicipalities());
    }
  }, [filters.district_id, dispatch]);

  useEffect(() => {
    if (formData.district_id) {
      dispatch(fetchMunicipalities(formData.district_id));
    } else {
      dispatch(clearMunicipalities());
    }
  }, [formData.district_id, dispatch]);

  const handlePageChange = (page) => {
    if (page >= 1 && page <= pagination.totalPages) {
      setCurrentPage(page);
      dispatch(fetchULBUsers(page, limit, filters));
    }
  };

  const handleRefresh = () => {
    dispatch(fetchULBUsers(currentPage, limit, filters));
    toast.success("Data refreshed!");
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData({ ...passwordData, [name]: value });
  };

  const resetForm = () => {
    setFormData({
      full_name: "",
      designation: "",
      username: "",
      email: "",
      phone: "",
      district_id: "",
      municipality_id: "",
    });
    setPasswordData({ newPassword: "", confirmPassword: "" });
  };

  const handleCreateSubmit = async (e) => {
    e.preventDefault();
    if (!isValidPhone(formData.phone)) {
      toast.error("Phone number must be exactly 10 digits");
      return;
    }
    try {
      const result = await dispatch(createULBUser(formData));
      if (result?.meta?.status) {
        setShowAddModal(false);
        resetForm();
        dispatch(fetchULBUsers(currentPage, limit, filters));
      }
    } catch (error) {
      // Error handled in action
    }
  };

  const handleEdit = (user) => {
    setSelectedUserId(user.id);
    setFormData({
      full_name: user.full_name || "",
      designation: user.designation || "",
      username: user.username || "",
      email: user.email || "",
      phone: user.phone || "",
      district_id: user.district_id || "",
      municipality_id: user.municipality_id || "",
    });
    if (user.district_id) {
      dispatch(fetchMunicipalities(user.district_id));
    }
    setShowEditModal(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!isValidPhone(formData.phone)) {
      toast.error("Phone number must be exactly 10 digits");
      return;
    }
    try {
      const result = await dispatch(updateULBUser(selectedUserId, formData));
      if (result?.meta?.status) {
        setShowEditModal(false);
        resetForm();
        dispatch(fetchULBUsers(currentPage, limit, filters));
      }
    } catch (error) {
      // Error handled in action
    }
  };

  const handlePasswordReset = (user) => {
    setSelectedUserId(user.id);
    setPasswordData({ newPassword: "", confirmPassword: "" });
    setShowPasswordModal(true);
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    try {
      const result = await dispatch(resetULBUserPassword(selectedUserId));
      if (result?.meta?.status) {
        setShowPasswordModal(false);
        setPasswordData({ newPassword: "", confirmPassword: "" });
      }
    } catch (error) {
      // Error handled in action
    }
  };

  const handleExportToExcel = async () => {
    try {
      const result = await dispatch(fetchULBUsers(1, 99999, filters));

      if (!result?.data || result.data.length === 0) {
        toast.error("No data to export");
        return;
      }

      const allUsers = result.data;

      const headers = [
        "ID",
        "Full Name",
        "Username",
        "Designation",
        "Email",
        "Phone",
        "District",
        "Municipality",
      ];

      const rows = allUsers.map((item) => ({
        ID: item.id || "N/A",
        "Full Name": item.full_name || "N/A",
        Username: item.username || "N/A",
        Designation: item.designation || "N/A",
        Email: item.email || "N/A",
        Phone: item.phone || "N/A",
        District: item.district_name || "N/A",
        Municipality: item.municipality_name || "N/A",
      }));

      const data = [headers, [], ...rows.map((row) => Object.values(row))];
      const ws = XLSX.utils.aoa_to_sheet(data);

      const colWidths = [
        { wch: 8 }, // ID
        { wch: 25 }, // Full Name
        { wch: 20 }, // Username
        { wch: 20 }, // Designation
        { wch: 30 }, // Email
        { wch: 15 }, // Phone
        { wch: 25 }, // District
        { wch: 25 }, // Municipality
      ];
      ws["!cols"] = colWidths;

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "ULBUsers");

      const date = new Date();
      const dateStr = `${String(date.getDate()).padStart(2, "0")}-${String(date.getMonth() + 1).padStart(2, "0")}-${date.getFullYear()}`;
      XLSX.writeFile(wb, `ULB_Users_${dateStr}.xlsx`);
      toast.success(`Exported ${allUsers.length} records successfully!`);
    } catch (error) {
      toast.error("Failed to export data");
    }
  };
  return (
    <div className="p-4 md:p-6 max-w-full mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold uppercase text-gray-900 dark:text-white">
            ULB-Level Users
          </h1>
          <p className="text-gray-600 dark:text-gray-300 mt-1">
            Manage ULB level administrators
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <button
            onClick={handleRefresh}
            className="flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-lg transition-colors"
          >
            <FiRefreshCw className="w-4 h-4" />
            Refresh
          </button>
          <button
            onClick={handleExportToExcel}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors"
          >
            <FiDownload className="w-4 h-4" />
            Export to Excel
          </button>
          <button
            onClick={() => setShowAddModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg transition-colors"
          >
            <FiPlus className="w-4 h-4" />
            Add ULB User
          </button>
        </div>
      </div>

      {/* Table Section */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <div className="flex items-center gap-4">
            <FiUsers className="w-5 h-5 text-gray-600 dark:text-gray-400" />
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              ULB Users List
            </h2>
            <span className="text-sm font-medium text-orange-800 dark:text-orange-200 border border-orange-200 dark:border-orange-700 px-3 py-1 rounded-md">
              TOTAL: {pagination.totalRecords || 0}
            </span>
            <div className="flex items-center gap-2 ml-4">
              <label className="text-sm text-gray-600 dark:text-gray-400">
                Show:
              </label>
              <select
                value={limit}
                onChange={(e) => {
                  setLimit(Number(e.target.value));
                  setCurrentPage(1);
                }}
                className="px-3 py-1 border-2 border-orange-200 dark:border-orange-700 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm font-medium min-w-[80px]"
              >
                {limitOptions.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <select
              value={filters.district_id}
              onChange={(e) => {
                const value = e.target.value;
                setFilters({
                  district_id: value,
                  municipality_id: "",
                });
                setCurrentPage(1);
              }}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
            >
              <option value="">All Districts</option>
              {districts.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name}
                </option>
              ))}
            </select>
            <select
              value={filters.municipality_id}
              onChange={(e) => {
                setFilters({
                  ...filters,
                  municipality_id: e.target.value,
                });
                setCurrentPage(1);
              }}
              disabled={!filters.district_id}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm disabled:opacity-50"
            >
              <option value="">All Municipalities</option>
              {municipalities.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name}
                </option>
              ))}
            </select>
            {(filters.district_id || filters.municipality_id) && (
              <button
                onClick={() => {
                  setFilters({
                    district_id: "",
                    municipality_id: "",
                  });
                  setCurrentPage(1);
                  dispatch(clearMunicipalities());
                }}
                className="px-3 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded-lg transition-colors text-sm flex items-center gap-1"
              >
                <FiX className="w-4 h-4" />
                Clear Filters
              </button>
            )}
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="w-8 h-8 border-4 border-orange-600 dark:border-orange-400 border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : users.length > 0 ? (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-gray-700">
                    {/* <th className="text-left uppercase italic text-base py-3 px-4 text-sm font-medium text-orange-800 dark:text-orange-400">
                      Sl.no.
                    </th> */}
                    <th className="text-left uppercase italic text-base py-3 px-4 text-sm font-medium text-orange-800 dark:text-orange-400">
                      ID
                    </th>
                    <th className="text-left uppercase italic text-base py-3 px-4 text-sm font-medium text-orange-800 dark:text-orange-400 min-w-[150px]">
                      Full Name
                    </th>
                    <th className="text-left uppercase italic text-base py-3 px-4 text-sm font-medium text-orange-800 dark:text-orange-400">
                      Username
                    </th>
                    <th className="text-left uppercase italic text-base py-3 px-4 text-sm font-medium text-orange-800 dark:text-orange-400">
                      Designation
                    </th>
                    <th className="text-left uppercase italic text-base py-3 px-4 text-sm font-medium text-orange-800 dark:text-orange-400">
                      Email
                    </th>
                    <th className="text-left uppercase italic text-base py-3 px-4 text-sm font-medium text-orange-800 dark:text-orange-400">
                      Phone
                    </th>
                    {/* <th className="text-center uppercase italic text-base py-3 px-4 text-sm font-medium text-orange-800 dark:text-orange-400">
                      Role
                    </th> */}
                    <th className="text-left uppercase italic text-base py-3 px-4 text-sm font-medium text-orange-800 dark:text-orange-400">
                      District
                    </th>
                    <th className="text-left uppercase italic text-base py-3 px-4 text-sm font-medium text-orange-800 dark:text-orange-400">
                      Municipality
                    </th>
                    <th className="text-left uppercase italic text-base py-3 px-4 text-sm font-medium text-orange-800 dark:text-orange-400 min-w-[150px]">
                      Created At
                    </th>
                    <th className="text-center uppercase italic text-base py-3 px-4 text-sm font-medium text-orange-800 dark:text-orange-400">
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((item, index) => (
                    <tr
                      key={item.id}
                      className="border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50"
                    >
                      {/* <td className="py-3 px-4 text-sm text-gray-500 dark:text-gray-400">
                        {(pagination.page - 1) * pagination.limit + index + 1}
                      </td> */}
                      <td className="py-3 px-4 text-sm font-mono italic text-orange-600 dark:text-orange-400">
                        {item.id}
                      </td>
                      <td className="py-3 px-4 text-sm font-medium text-gray-900 dark:text-white">
                        {item.full_name || "N/A"}
                      </td>
                      <td className="py-3 px-4 text-sm  italic text-gray-600 dark:text-gray-300">
                        {item.username || "N/A"}
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-300">
                        {item.designation || "N/A"}
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-300">
                        {item.email || "N/A"}
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-300">
                        {item.phone || "N/A"}
                      </td>
                      {/* <td className="py-3 px-4 text-sm">
                        <span className="inline-block px-3 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 rounded-full text-xs font-medium whitespace-nowrap">
                          {item.role || "N/A"}
                        </span>
                      </td> */}
                      <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-300">
                        {item.district_name || "N/A"}
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-300">
                        {item.municipality_name || "N/A"}
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-300">
                        {item.created_at ? (
                          <span className="font-mono text-xs text-gray-700 dark:text-gray-300">
                            {new Date(item.created_at).toLocaleDateString(
                              "en-IN",
                            )}
                          </span>
                        ) : (
                          <span className="text-gray-400">N/A</span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-sm">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleEdit(item)}
                            className="flex items-center gap-1.5 px-3 py-1 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded transition-colors"
                          >
                            <FiEdit className="w-3.5 h-3.5" />
                            Edit
                          </button>
                          <button
                            onClick={() => handlePasswordReset(item)}
                            className="flex items-center gap-1.5 px-3 py-1 text-xs font-medium text-white bg-orange-600 hover:bg-orange-700 rounded transition-colors"
                          >
                            <FiLock className="w-3.5 h-3.5" />
                            Reset
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {pagination.totalPages > 1 && (
              <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  Showing {(pagination.page - 1) * pagination.limit + 1} to{" "}
                  {Math.min(
                    pagination.page * pagination.limit,
                    pagination.totalRecords,
                  )}{" "}
                  of {pagination.totalRecords} entries
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="p-2 rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-gray-600 dark:text-gray-400"
                  >
                    <FiChevronLeft className="w-4 h-4" />
                  </button>
                  <span className="px-4 py-2 bg-orange-600 text-white rounded-lg text-sm font-medium">
                    {currentPage}
                  </span>
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === pagination.totalPages}
                    className="p-2 rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-gray-600 dark:text-gray-400"
                  >
                    <FiChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-12">
            <FiUsers className="w-16 h-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
            <p className="text-gray-500 dark:text-gray-400">
              No ULB users found
            </p>
            <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">
              Click "Add ULB User" to create a new ULB level administrator
            </p>
          </div>
        )}
      </div>

      {/* Add Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 dark:bg-white-100/30 backdrop-blur-sm">
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 max-w-2xl w-full mx-4 shadow-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                Add ULB User
              </h3>
              <button
                onClick={() => {
                  setShowAddModal(false);
                  resetForm();
                }}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                <FiX className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <form onSubmit={handleCreateSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    name="full_name"
                    value={formData.full_name}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Designation *
                  </label>
                  <input
                    type="text"
                    name="designation"
                    value={formData.designation}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Username *
                  </label>
                  <input
                    type="text"
                    name="username"
                    value={formData.username}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Email *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Phone *
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={(e) => {
                    const value = e.target.value
                      .replace(/\D/g, "")
                      .slice(0, 10);
                    setFormData({ ...formData, phone: value });
                    if (value.length > 0 && value.length !== 10) {
                      setEditPhoneError(
                        "Phone number must be exactly 10 digits",
                      );
                    } else {
                      setEditPhoneError("");
                    }
                  }}
                  required
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white ${
                    editPhoneError
                      ? "border-red-500 dark:border-red-500"
                      : "border-gray-300 dark:border-gray-600"
                  }`}
                  placeholder="Enter 10-digit phone number"
                />
                {editPhoneError && (
                  <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                    {editPhoneError}
                  </p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    District *
                  </label>
                  <select
                    name="district_id"
                    value={formData.district_id}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    <option value="">Select District</option>
                    {districts.map((d) => (
                      <option key={d.id} value={d.id}>
                        {d.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Municipality *
                  </label>
                  <select
                    name="municipality_id"
                    value={formData.municipality_id}
                    onChange={handleInputChange}
                    required
                    disabled={!formData.district_id}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white disabled:opacity-50"
                  >
                    <option value="">Select Municipality</option>
                    {municipalities.map((m) => (
                      <option key={m.id} value={m.id}>
                        {m.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddModal(false);
                    resetForm();
                  }}
                  className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg transition-colors"
                >
                  Create User
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 dark:bg-white-100/30 backdrop-blur-sm">
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 max-w-2xl w-full mx-4 shadow-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                Edit ULB User
              </h3>
              <button
                onClick={() => {
                  setShowEditModal(false);
                  resetForm();
                }}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                <FiX className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <form onSubmit={handleEditSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    name="full_name"
                    value={formData.full_name}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Designation *
                  </label>
                  <input
                    type="text"
                    name="designation"
                    value={formData.designation}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Username *
                  </label>
                  <input
                    type="text"
                    name="username"
                    value={formData.username}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Email *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Phone *
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={(e) => {
                    const value = e.target.value
                      .replace(/\D/g, "")
                      .slice(0, 10);
                    setFormData({ ...formData, phone: value });
                    if (value.length > 0 && value.length !== 10) {
                      setPhoneError("Phone number must be exactly 10 digits");
                    } else {
                      setPhoneError("");
                    }
                  }}
                  required
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white ${
                    phoneError
                      ? "border-red-500 dark:border-red-500"
                      : "border-gray-300 dark:border-gray-600"
                  }`}
                  placeholder="Enter 10-digit phone number"
                />
                {phoneError && (
                  <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                    {phoneError}
                  </p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    District *
                  </label>
                  <select
                    name="district_id"
                    value={formData.district_id}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    <option value="">Select District</option>
                    {districts.map((d) => (
                      <option key={d.id} value={d.id}>
                        {d.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Municipality *
                  </label>
                  <select
                    name="municipality_id"
                    value={formData.municipality_id}
                    onChange={handleInputChange}
                    required
                    disabled={!formData.district_id}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white disabled:opacity-50"
                  >
                    <option value="">Select Municipality</option>
                    {municipalities.map((m) => (
                      <option key={m.id} value={m.id}>
                        {m.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                <button
                  type="button"
                  onClick={() => {
                    setShowEditModal(false);
                    resetForm();
                  }}
                  className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg transition-colors"
                >
                  Update User
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Reset Password Modal */}
      {showPasswordModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 dark:bg-white-100/30 backdrop-blur-sm">
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 max-w-md w-full mx-4 shadow-2xl">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                Reset Password
              </h3>
              <button
                onClick={() => {
                  setShowPasswordModal(false);
                  setPasswordData({ newPassword: "", confirmPassword: "" });
                }}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                <FiX className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <div className="text-center py-4">
              <p className="text-gray-600 dark:text-gray-300 mb-2">
                Are you sure you want to reset the password for this user?
              </p>
              {/* <p className="text-sm text-gray-500 dark:text-gray-400">
                The new password will be sent to the user's email.
              </p> */}
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
              <button
                type="button"
                onClick={() => {
                  setShowPasswordModal(false);
                  setPasswordData({ newPassword: "", confirmPassword: "" });
                }}
                className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handlePasswordSubmit}
                className="px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg transition-colors"
              >
                Reset Password
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ULBUsers;
